import React, { useEffect, useState } from 'react';
import {
  Divider,
  Tab,
  Tabs,
  Typography,
  makeStyles,
  Box,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { PageToolbar } from 'components/PageToolbar';
import { DepartmentDetailsTab } from 'features/departments/departmentTabsContent/DepartmentDetailsTab';
import { DepartmentContactsTab } from 'features/departments/departmentTabsContent/DepartmentContactsTab';
import { DepartmentUsersTab } from 'features/departments/departmentTabsContent/DepartmentUsersTab';
import { ContractTab } from 'features/departments/departmentTabsContent/ContractTab';
import { DepartmentsTable } from 'features/departments/DepartmentsTable';
import { history } from 'helpers/history';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { departmentActions } from 'actions/departmentActions';
import { useParams } from 'react-router-dom';
import { getAuthorizedDepartments } from 'helpers/authorizedDepartments';

const useStyles = makeStyles(() => ({
  toolbarTitle: {
    minHeight: '1.8rem',
  },
  alignRight: {
    textAlign: 'right',
  },
  buttonClass: {
    marginLeft: 0,
  },
  plusIcon: {
    display: 'inline-block',
    fontSize: '1.5rem',
    lineHeight: 0,
    marginRight: '0.5rem',
  },

  flexContainer: {
    justifyContent: 'space-between',
  },

  root: {
    marginBottom: '-1rem',
  },

  tabPanel: {
    flex: 1,
  },

  tabBox: {
    height: '100%',
  },
}));

interface ParamTypes {
  deptId: string;
  orgId: string;
}
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  const classes = useStyles();
  return (
    <div
      className={classes.tabPanel}
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box className={classes.tabBox} p={3}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const Department = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { deptId, orgId } = useParams<ParamTypes>();
  const { t } = useTranslation(['translations']);
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const tabIdParam = urlParams.get('tab');
  const tabId = tabIdParam && +tabIdParam;
  const [tab, setTab] = useState(tabId || 0);

  const departmentName: string | undefined = useSelector(
    (state: ApplicationState) => state.department?.department?.name,
  );

  const userAccess = useSelector(
    (state: ApplicationState) => state.isLoggedIn.userAccess,
  );
  const orgDepartments = useSelector(
    (state: ApplicationState) => state.organization?.organization?.departments,
  );
  const accessibleDepartments = useSelector(
    (state: ApplicationState) => state.isLoggedIn?.userAccess?.departments,
  );

  const departments = getAuthorizedDepartments(
    orgId,
    userAccess,
    orgDepartments,
    accessibleDepartments,
  );

  // eslint-disable-next-line @typescript-eslint/ban-types
  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTab(newValue);
    history.push(`${location.pathname}?tab=${newValue}`);
  };

  useEffect(() => {
    if (
      deptId === 'all' &&
      (userAccess?.organizations?.find(
        (organization) => organization.id === orgId,
      ) ||
        userAccess?.isGlobalAdmin)
    ) {
      history.replace(`/organization/${orgId}`);
    }
  });

  useEffect(() => {
    dispatch(departmentActions.clearDepartment());
    if (deptId && deptId !== 'all') {
      dispatch(departmentActions.getDepartmentById(deptId));
    }
    setTab(tabId || 0);
  }, [deptId]);

  useEffect(() => {
    setTab(tabId || 0);
  }, [tabId]);

  return (
    <>
      {deptId === 'all' ? (
        <>
          <PageToolbar>
            <Typography variant="subtitle1" className={classes.toolbarTitle}>
              {t('department_page.select_department_title')}
            </Typography>
            <Divider />
          </PageToolbar>
          <DepartmentsTable departments={departments} inTab={false} />
        </>
      ) : (
        <>
          <PageToolbar>
            <Typography variant="subtitle1" className={classes.toolbarTitle}>
              {departmentName}
            </Typography>
            <Divider />
            <Tabs
              value={tab}
              onChange={handleChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              classes={{
                flexContainer: classes.flexContainer,
                root: classes.root,
              }}
            >
              <Tab
                label={t('department_page.tabs.department_information')}
                icon={<div className="organization-details-icon"></div>}
                {...a11yProps(0)}
              />
              <Tab
                label={t('department_page.tabs.contract')}
                icon={<div className="contract-icon"></div>}
                {...a11yProps(1)}
              />
              <Tab
                label={t('department_page.tabs.contacts')}
                icon={<div className="contacts-icon"></div>}
                {...a11yProps(2)}
              />
              <Tab
                label={t('department_page.tabs.users')}
                icon={<div className="users-icon"></div>}
                {...a11yProps(3)}
              />
            </Tabs>
          </PageToolbar>
          <TabPanel value={tab} index={0}>
            <DepartmentDetailsTab />
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <ContractTab />
          </TabPanel>
          <TabPanel value={tab} index={2}>
            <DepartmentContactsTab />
          </TabPanel>
          <TabPanel value={tab} index={3}>
            <DepartmentUsersTab />
          </TabPanel>
        </>
      )}
    </>
  );
};

export { Department };
